import { ArrowRight, CalendarDays, CircleAlert, Construction, Drama, TrainFront, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router';
import { apiPaths, type EventsResponse, type TimelineResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import type { LocalEvent, TimelineItem } from '../../../shared/domain/index.ts';
import { FRESHNESS_POLICIES } from '../../../shared/freshness/index.ts';
import { endOfLocalDay, localDate } from '../../../shared/lib/time.ts';
import { SectionCard } from '../../components/SectionCard.tsx';
import { SkeletonRows } from '../../components/Skeleton.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatDayTime, formatTime } from '../../lib/format.ts';

/** Icône et teinte fonctionnelle par type : violet culture, orange travaux, bleu transports, jaune → rouge alertes. */
const KIND: Readonly<Record<TimelineItem['kind'], { icon: LucideIcon; label: string; tone: string }>> = {
  event: { icon: Drama, label: 'Événement', tone: 'culture' },
  roadwork: { icon: Construction, label: 'Travaux', tone: 'warn' },
  alert: { icon: CircleAlert, label: 'Alerte', tone: 'weather' },
  disruption: { icon: TrainFront, label: 'Transports', tone: 'transport' },
};

const ALERT_TONE = { info: 'weather', warning: 'warn', critical: 'danger' } as const;

function TimelineEntry({ item }: { item: TimelineItem }) {
  const { icon: Icon, label } = KIND[item.kind];
  const tone = item.kind === 'alert' && item.severity !== undefined ? ALERT_TONE[item.severity] : KIND[item.kind].tone;
  const external = item.href?.startsWith('http') ?? false;
  const title =
    item.href === undefined ? (
      item.title
    ) : external ? (
      <a href={item.href} rel="noopener noreferrer" target="_blank">
        {item.title}
      </a>
    ) : (
      <Link to={item.href}>{item.title}</Link>
    );
  return (
    <li className="timeline__item">
      <span className="timeline__time">
        {item.at === null ? (
          <span>
            Toute
            <br />
            la journée
          </span>
        ) : (
          <time dateTime={item.at}>{formatTime(item.at)}</time>
        )}
      </span>
      <span className={`timeline__icon tone--${tone}`}>
        <Icon size={18} aria-hidden="true" />
      </span>
      <span className="timeline__body">
        <span className="visually-hidden">{label} : </span>
        <span className="timeline__title">{title}</span>
        {item.subtitle !== undefined && <span className="timeline__subtitle">{item.subtitle}</span>}
      </span>
    </li>
  );
}

/** Au-delà de ce nombre d'éléments du jour, pas besoin d'annoncer la suite. */
const TARGET_ITEMS = 4;

/** « Prochainement » : les événements suivants de l'agenda, quand la journée est calme. Données réelles uniquement. */
function Upcoming({ events }: { events: readonly LocalEvent[] }) {
  if (events.length === 0) return null;
  return (
    <div className="upcoming">
      <h3 className="upcoming__title">Prochainement</h3>
      <ul className="upcoming__list">
        {events.map((e) => (
          <li key={e.id}>
            <time dateTime={e.startsAt}>{formatDayTime(e.startsAt)}</time>
            {e.url === undefined ? (
              <span>{e.title}</span>
            ) : (
              <a href={e.url} rel="noopener noreferrer" target="_blank">
                {e.title}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TodayTimeline({ className }: { className?: string }) {
  const city = DEFAULT_CITY;
  // La date est recalculée à chaque rendu : la timeline bascule d'elle-même à minuit.
  const today = localDate(new Date(), city.timeZone);
  const timeline = useApi<TimelineResponse>(apiPaths.timeline(city.slug, today), FRESHNESS_POLICIES.events.refreshMs);
  const events = useApi<EventsResponse>(apiPaths.events(city.slug), FRESHNESS_POLICIES.events.refreshMs);
  const endOfToday = Date.parse(endOfLocalDay(today, city.timeZone));
  const upcomingFor = (count: number) =>
    count >= TARGET_ITEMS
      ? []
      : (events.data?.events.data ?? [])
          .filter((e) => Date.parse(e.startsAt) > endOfToday)
          .toSorted((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
          .slice(0, TARGET_ITEMS - count);
  return (
    <SectionCard
      title="Aujourd’hui à Ermont"
      icon={<CalendarDays size={22} aria-hidden="true" />}
      kind="events"
      subject="de l’agenda"
      section={timeline.data?.items}
      loading={timeline.loading}
      fetchError={timeline.error}
      skeleton={<SkeletonRows rows={4} badge="round" />}
      className={className}
      actions={
        <Link className="text-link" to="/agenda">
          Voir tout l’agenda <ArrowRight size={16} aria-hidden="true" />
        </Link>
      }
    >
      {(items) => (
        <>
          {items.length === 0 ? (
            <div className="empty-state">
              <CalendarDays size={28} aria-hidden="true" />
              <p>Aucun événement prévu aujourd’hui.</p>
            </div>
          ) : (
            <ol className="timeline">
              {items.map((item) => (
                <TimelineEntry key={item.id} item={item} />
              ))}
            </ol>
          )}
          <Upcoming events={upcomingFor(items.length)} />
        </>
      )}
    </SectionCard>
  );
}
